import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon,
  List, 
  ListOrdered, 
  Link as LinkIcon,
  Heading2,
  Heading3,
  Undo,
  Redo,
  FileCode,
  Eye,
  Minus
} from 'lucide-react';
import { useEffect, useCallback, useState } from 'react';

const MenuButton = ({ onClick, active, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 hover:bg-muted transition-colors ${
      active ? 'bg-muted text-primary' : 'text-muted-foreground'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

const MenuBar = ({ editor, showHtml, onToggleView }) => {
  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL eingeben:', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-border bg-muted/50">
      {!showHtml && (
        <>
          <MenuButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            title="Fett (Ctrl+B)"
          >
            <Bold size={18} />
          </MenuButton>
          
          <MenuButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            title="Kursiv (Ctrl+I)"
          >
            <Italic size={18} />
          </MenuButton>
          
          <MenuButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive('underline')}
            title="Unterstrichen (Ctrl+U)"
          >
            <UnderlineIcon size={18} />
          </MenuButton>

          <div className="w-px h-6 bg-border mx-1" />
          
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive('heading', { level: 2 })}
            title="Überschrift 2"
          >
            <Heading2 size={18} />
          </MenuButton>
          
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            active={editor.isActive('heading', { level: 3 })}
            title="Überschrift 3"
          >
            <Heading3 size={18} />
          </MenuButton>

          <div className="w-px h-6 bg-border mx-1" />
          
          <MenuButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            title="Aufzählung"
          >
            <List size={18} />
          </MenuButton>
          
          <MenuButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
            title="Nummerierte Liste"
          >
            <ListOrdered size={18} />
          </MenuButton>

          <div className="w-px h-6 bg-border mx-1" />

          <MenuButton
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            title="Horizontale Linie"
          >
            <Minus size={18} />
          </MenuButton>
          
          <MenuButton
            onClick={setLink}
            active={editor.isActive('link')}
            title="Link einfügen"
          >
            <LinkIcon size={18} />
          </MenuButton>

          <div className="w-px h-6 bg-border mx-1" />
          
          <MenuButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Rückgängig (Ctrl+Z)"
          >
            <Undo size={18} />
          </MenuButton>
          
          <MenuButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Wiederholen (Ctrl+Y)"
          >
            <Redo size={18} />
          </MenuButton>
        </>
      )}

      <div className="flex-1" />
      
      <MenuButton
        onClick={onToggleView}
        active={showHtml}
        title={showHtml ? "Vorschau anzeigen" : "HTML bearbeiten"}
      >
        {showHtml ? <Eye size={18} /> : <FileCode size={18} />}
      </MenuButton>
    </div>
  );
};

export const WysiwygEditor = ({ value, onChange, placeholder = 'Inhalt eingeben...' }) => {
  const [showHtml, setShowHtml] = useState(false);
  const [htmlValue, setHtmlValue] = useState(value || '');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
          HTMLAttributes: {
            class: 'editor-bullet-list',
          },
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
          HTMLAttributes: {
            class: 'editor-ordered-list',
          },
        },
        listItem: {
          HTMLAttributes: {
            class: 'editor-list-item',
          },
        },
        paragraph: {
          HTMLAttributes: {
            class: 'editor-paragraph',
          },
        },
        horizontalRule: {
          HTMLAttributes: {
            class: 'editor-hr',
          },
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'editor-link',
        },
      }),
      Underline,
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setHtmlValue(html);
      onChange(html);
    },
  });

  // Update content when value prop changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
      setHtmlValue(value || '');
    }
  }, [value, editor]);

  const handleToggleView = useCallback(() => {
    if (showHtml && editor) {
      // Switching from HTML to WYSIWYG - apply HTML changes
      editor.commands.setContent(htmlValue, false);
    }
    setShowHtml(!showHtml);
  }, [showHtml, htmlValue, editor]);

  const handleHtmlChange = useCallback((e) => {
    const newHtml = e.target.value;
    setHtmlValue(newHtml);
    onChange(newHtml);
  }, [onChange]);

  return (
    <div className="wysiwyg-wrapper border border-border bg-card overflow-hidden">
      <MenuBar 
        editor={editor} 
        showHtml={showHtml}
        onToggleView={handleToggleView}
      />
      
      {showHtml ? (
        <textarea
          value={htmlValue}
          onChange={handleHtmlChange}
          className="w-full min-h-[250px] p-4 bg-background text-foreground font-mono text-sm focus:outline-none resize-y border-0"
          placeholder="HTML-Code hier eingeben..."
        />
      ) : (
        <EditorContent editor={editor} />
      )}
      
      <style>{`
        /* Editor Container */
        .wysiwyg-wrapper .tiptap-editor {
          min-height: 250px;
          padding: 1rem;
          outline: none;
          font-family: Arial, sans-serif;
          font-size: 16px;
          line-height: 1.6;
          color: hsl(var(--foreground));
        }

        /* Focus state */
        .wysiwyg-wrapper .tiptap-editor:focus {
          outline: none;
        }

        /* Paragraphs */
        .wysiwyg-wrapper .tiptap-editor p {
          margin: 0 0 1em 0;
          line-height: 1.6;
        }

        .wysiwyg-wrapper .tiptap-editor p:last-child {
          margin-bottom: 0;
        }

        /* Headings */
        .wysiwyg-wrapper .tiptap-editor h2 {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 1.4em;
          font-weight: 700;
          margin: 1.5em 0 0.75em 0;
          letter-spacing: 0.01em;
          line-height: 1.3;
          color: hsl(var(--foreground));
        }

        .wysiwyg-wrapper .tiptap-editor h3 {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 1.2em;
          font-weight: 700;
          margin: 1.25em 0 0.5em 0;
          line-height: 1.3;
          color: hsl(var(--foreground));
        }

        .wysiwyg-wrapper .tiptap-editor h2:first-child,
        .wysiwyg-wrapper .tiptap-editor h3:first-child {
          margin-top: 0;
        }

        /* Bullet Lists */
        .wysiwyg-wrapper .tiptap-editor ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin: 0 0 1em 0;
        }

        .wysiwyg-wrapper .tiptap-editor ul ul {
          list-style-type: circle;
          margin: 0.5em 0;
        }

        .wysiwyg-wrapper .tiptap-editor ul ul ul {
          list-style-type: square;
        }

        /* Ordered Lists */
        .wysiwyg-wrapper .tiptap-editor ol {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin: 0 0 1em 0;
        }

        .wysiwyg-wrapper .tiptap-editor ol ol {
          list-style-type: lower-alpha;
          margin: 0.5em 0;
        }

        /* List Items */
        .wysiwyg-wrapper .tiptap-editor li {
          margin: 0.25em 0;
          padding-left: 0.25em;
          line-height: 1.5;
        }

        .wysiwyg-wrapper .tiptap-editor li p {
          margin: 0;
          display: inline;
        }

        .wysiwyg-wrapper .tiptap-editor li > p:first-child {
          display: inline;
        }

        /* Links */
        .wysiwyg-wrapper .tiptap-editor a {
          color: hsl(var(--primary));
          text-decoration: underline;
          cursor: pointer;
        }

        .wysiwyg-wrapper .tiptap-editor a:hover {
          opacity: 0.8;
        }

        /* Bold, Italic, Underline */
        .wysiwyg-wrapper .tiptap-editor strong {
          font-weight: 700;
        }

        .wysiwyg-wrapper .tiptap-editor em {
          font-style: italic;
        }

        .wysiwyg-wrapper .tiptap-editor u {
          text-decoration: underline;
        }

        /* Horizontal Rule */
        .wysiwyg-wrapper .tiptap-editor hr {
          border: none;
          border-top: 2px solid hsl(var(--border));
          margin: 1.5em 0;
        }

        /* Blockquote */
        .wysiwyg-wrapper .tiptap-editor blockquote {
          border-left: 4px solid hsl(var(--primary));
          padding-left: 1em;
          margin: 1em 0;
          font-style: italic;
          color: hsl(var(--muted-foreground));
        }

        /* Code */
        .wysiwyg-wrapper .tiptap-editor code {
          background: hsl(var(--muted));
          padding: 0.2em 0.4em;
          border-radius: 3px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.9em;
        }

        .wysiwyg-wrapper .tiptap-editor pre {
          background: hsl(var(--muted));
          padding: 1em;
          border-radius: 4px;
          overflow-x: auto;
          margin: 1em 0;
        }

        .wysiwyg-wrapper .tiptap-editor pre code {
          background: none;
          padding: 0;
        }

        /* Placeholder */
        .wysiwyg-wrapper .tiptap-editor p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: hsl(var(--muted-foreground));
          pointer-events: none;
          height: 0;
        }

        /* Selection */
        .wysiwyg-wrapper .tiptap-editor ::selection {
          background: hsl(var(--primary) / 0.2);
        }
      `}</style>
    </div>
  );
};

export default WysiwygEditor;
